<script lang="ts">
	import ShoppingBag from '$lib/icons/shoppingBag.svelte';
	import Mill from '$lib/logos/mill.svelte';
	import { shoppingCart } from '$lib/state/shoppingCart.svelte';
	import Blok from '$lib/storyblok/blok.svelte';
	import { editable } from '$lib/storyblok/editable';
	import { scrollLock } from '$lib/utils/scrollLock.svelte';
	import type { ISbStoryData } from '@storyblok/js';
	import { innerWidth } from 'svelte/reactivity/window';
	import { fade } from 'svelte/transition';
	import { type ButtonV3Props } from '../buttonV3.svelte';
	import NavMenuBarLink from './navMenuBarLink.svelte';
	import FlyIn from './flyIn.svelte';
	import BurgerMenuButton from './burgerMenuButton.svelte';
	import captureClick from '$lib/actions/captureClick';

	interface Props {
		content: ISbStoryData<{
			theme: 'light' | 'dark';
			center_navigation: ISbStoryData['content'][];
			right_navigation: ISbStoryData<ButtonV3Props | NavMenuBarLink>['content'][];
			highlighted_links: ISbStoryData['content'][];
			highlighted_links_label: string;
			standard_links_left: ISbStoryData['content'][];
			standard_links_right: ISbStoryData['content'][];
			teaser_cards: ISbStoryData['content'][];
		}>['content'];
		passedHeader?: boolean;
	}

	let { content, passedHeader = false }: Props = $props();
	let expanded = $state(false);
	let isMobile = $derived(!innerWidth.current || innerWidth.current < 1024);
	let foldedHeaderHeight = $state(0);

	$effect(() => {
		if (expanded && isMobile) {
			scrollLock.lock();
		} else {
			scrollLock.unlock();
		}
	});

	let activeColor: 'white' | 'brown' = $derived(
		content.theme === 'light' && !expanded && !passedHeader ? 'white' : 'brown'
	);
</script>

{#snippet standardLinks()}
	<div class="flex flex-1 flex-col gap-y-1 lg:gap-y-3">
		{#each content.standard_links_left as link, index}
			<FlyIn play={expanded} {index}>
				<Blok content={link} />
			</FlyIn>
		{/each}
	</div>
	<div class="flex flex-1 flex-col gap-y-1 empty:hidden lg:gap-y-3">
		{#each content.standard_links_right as link, index}
			<FlyIn play={expanded} {index}>
				<Blok content={link} />
			</FlyIn>
		{/each}
	</div>
{/snippet}

{#if expanded}
	<!-- svelte-ignore a11y_no_static_element_interactions -->
	<!-- svelte-ignore a11y_click_events_have_key_events -->
	<div
		onclick={() => (expanded = false)}
		transition:fade
		class="fixed z-30 h-screen w-screen bg-black/[.48]"
	></div>
{/if}

<header
	use:editable={content}
	class={[
		'nav-expand group relative z-40 flex max-h-svh flex-col overflow-hidden text-brown-1 transition-all duration-400 lg:m-5 lg:rounded-2xl lg:hover:bg-white',
		{
			'bg-white': expanded
		},
		activeColor === 'white' ? 'text-white lg:hover:text-brown-1' : 'text-brown-1',
		passedHeader ? ' bg-white shadow-[0_1px_8px_0_rgba(0,0,0,0.12)]' : ''
	]}
	style="transform: translateY(var(--banner-height));"
>
	<div
		id="site-header"
		class="flex justify-between px-5 py-[18px] lg:px-8"
		bind:clientHeight={foldedHeaderHeight}
	>
		<div class="flex flex-1 items-center justify-start">
			<a class="pointer-events-auto" title="Home" href="/">
				<Mill
					class={[
						'h-8 transition-colors duration-400 lg:h-10',
						activeColor === 'white' ? 'text-white lg:group-hover:text-green-3' : 'text-green-3'
					]}
				/>
			</a>
		</div>
		<div class="flex flex-1 items-center justify-center gap-x-2 max-lg:hidden">
			{#each content.center_navigation as navItem}
				<Blok content={navItem} />
			{/each}
		</div>
		<div class="flex flex-1 items-center justify-end lg:gap-x-2">
			<div class={['flex gap-x-2', !passedHeader && 'max-lg:hidden']}>
				{#each content.right_navigation as navItem}
					<Blok content={navItem} />
				{/each}
			</div>

			<button
				class={[
					'flex items-center gap-x-2 px-5 py-1 text-[16px] tracking-[-.16px] transition-all hover:opacity-60 lg:py-2 lg:text-[18px] lg:tracking-[-.18px]',
					activeColor === 'white'
						? 'text-white duration-400 lg:group-hover:text-brown-1'
						: 'text-brown-1'
				]}
				use:captureClick
				title="Open cart"
				onclick={() => {
					shoppingCart.open();
					expanded = false;
				}}
			>
				<ShoppingBag class="h-5 w-5" />
				<p>Cart</p>
			</button>

			<BurgerMenuButton
				open={expanded}
				color={activeColor}
				onclick={() => {
					expanded = !expanded;
				}}
			/>
		</div>
	</div>

	<div
		class={[
			'grid transition-[grid-template-rows] duration-600 ease-[cubic-bezier(0.22,1,0.36,1)]',
			expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
		]}
	>
		<div
			class={[
				'scrollbar-hide mx-5 flex flex-col overflow-y-scroll transition-[border-color] lg:mx-8 lg:flex-row lg:justify-between lg:gap-x-20 lg:border-t',
				expanded ? 'border-brown-5' : 'border-transparent'
			]}
			style="max-height: calc(100svh - {foldedHeaderHeight}px);"
		>
			<div class="flex flex-col gap-y-1 py-7 lg:hidden">
				{#each content.center_navigation as navItem}
					<Blok content={navItem} />
				{/each}
			</div>
			<div
				class={[
					'flex flex-col border-brown-5 max-lg:border-y max-lg:py-7 lg:py-8',
					content.teaser_cards.length > 1 && 'justify-between'
				]}
			>
				<div>
					<FlyIn play={expanded}>
						<p class="text-[16px]/[150%] tracking-[-.16px] text-brown-1/50 max-lg:mb-2 lg:mb-4">
							{content.highlighted_links_label}
						</p>
					</FlyIn>
					<div class="flex flex-col gap-y-1 lg:gap-y-6">
						{#each content.highlighted_links as link, index}
							<FlyIn play={expanded} {index}>
								<Blok content={link} />
							</FlyIn>
						{/each}
					</div>
				</div>

				{#if content.teaser_cards.length > 1}
					<div class="mt-5 flex gap-x-5 lg:mt-15 lg:gap-x-20">
						{@render standardLinks()}
					</div>
				{/if}
			</div>

			{#if content.teaser_cards.length === 1}
				<div
					class="flex gap-x-5 pb-5 pt-7 lg:my-8 lg:min-w-[300px] lg:gap-x-28 lg:pt-[42px] 2xl:lg:gap-x-44"
				>
					{@render standardLinks()}
				</div>
			{/if}
			<FlyIn play={expanded}>
				<div
					class={[
						'gap-y-3 max-lg:pb-5 lg:my-8 lg:flex lg:aspect-3/2 lg:flex-col lg:gap-y-5',
						content.teaser_cards.length === 1 ? 'lg:max-w-[467px]' : 'lg:max-w-[750px]'
					]}
				>
					<div
						class={[
							'w-full max-lg:gap-y-3 lg:flex lg:flex-row lg:gap-x-5',
							content.teaser_cards.length > 2 ? 'lg:h-1/2' : 'h-full'
						]}
					>
						{#if content.teaser_cards[0]}
							<div
								class={[
									'max-lg:aspect-3/2',
									content.teaser_cards.length === 1 ? 'lg:w-full' : 'lg:w-1/2'
								]}
							>
								<Blok content={content.teaser_cards[0]} />
							</div>
						{/if}
						{#if content.teaser_cards[1]}
							<div class="max-lg:mt-3 max-lg:aspect-3/2 lg:w-1/2">
								<Blok content={content.teaser_cards[1]} />
							</div>
						{/if}
					</div>
					{#if content.teaser_cards[2]}
						<div class="max-lg:mt-3 max-lg:aspect-3/2 lg:h-1/2">
							<Blok content={content.teaser_cards[2]} />
						</div>
					{/if}
				</div>
			</FlyIn>
		</div>
	</div>
</header>
